import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { 
  Award, 
  Lightbulb, 
  Factory, 
  CheckCircle, 
  Users,
  Target,
  TrendingUp,
  Shield
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const timelineRef = useRef([]);
  const strengthsRef = useRef([]);

  useEffect(() => {
    // Animate timeline items
    timelineRef.current.forEach((item, index) => {
      if (item) {
        gsap.from(item, {
          scrollTrigger: {
            trigger: item,
            start: 'top 85%',
          },
          x: index % 2 === 0 ? -60 : 60,
          opacity: 0,
          duration: 0.8,
          ease: 'power3.out'
        });
      }
    });

    // Animate strength cards
    strengthsRef.current.forEach((card, index) => {
      if (card) {
        gsap.from(card, {
          scrollTrigger: {
            trigger: card,
            start: 'top 85%',
          },
          y: 50,
          opacity: 0,
          duration: 0.8,
          delay: index * 0.1,
          ease: 'power3.out'
        });
      }
    });
  }, []);

  const timeline = [
    {
      era: 'Early Years',
      title: 'Foundation',
      description: 'Established as a packaging manufacturer focused on delivering dependable solutions to local businesses.'
    },
    {
      era: 'Expansion Phase',
      title: 'Growth',
      description: 'Investment in advanced machinery and expansion of production capabilities to serve a broader market.'
    },
    {
      era: 'Modernization',
      title: 'Innovation',
      description: 'Adoption of modern manufacturing processes and introduction of in-house design support services.'
    },
    {
      era: 'Present Day',
      title: 'Excellence',
      description: 'Serving a wide range of industries with comprehensive end-to-end packaging solutions.'
    }
  ];

  const strengths = [
    {
      icon: Lightbulb,
      title: 'In-House Design Expertise',
      description: 'Our team works closely with clients to translate ideas into practical packaging solutions that align with brand identity and functional requirements.'
    },
    {
      icon: Target,
      title: 'Prototyping Capabilities',
      description: 'We develop physical samples to ensure structural integrity, visual alignment, and practical functionality before moving to production.'
    },
    {
      icon: Factory,
      title: 'Advanced Manufacturing',
      description: 'Modern equipment and skilled processes ensure consistent output quality across all production volumes and specifications.'
    },
    {
      icon: Shield,
      title: 'Material Knowledge',
      description: 'Deep understanding of paper, board, and packaging materials enables optimal selection for performance and cost-effectiveness.'
    },
    {
      icon: CheckCircle,
      title: 'Quality Assurance',
      description: 'Strict quality control measures at every stage of production maintain the standards clients expect from their packaging partner.'
    },
    {
      icon: Users,
      title: 'Client Partnership',
      description: 'Long-term relationships built on reliability, transparency, and consistent delivery of quality packaging solutions.'
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider">About Sagar Pack</p>
            <h1 className="text-5xl lg:text-6xl font-bold text-navy-900 leading-tight">
              Three Decades of
              <br />
              <span className="text-gray-600">Packaging Expertise</span>
            </h1>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto space-y-6 text-lg text-gray-700 leading-relaxed">
            <p>
              Sagar Pack has been delivering reliable and high-quality packaging solutions for more than thirty years. 
              With deep industry knowledge and hands-on manufacturing experience, the company has grown into a trusted 
              packaging partner for businesses across multiple sectors.
            </p>
            <p>
              From emerging brands to established enterprises, Sagar Pack supports organizations in creating packaging 
              that combines functionality, durability, and visual appeal.
            </p>
            <p>
              Our focus has always been on precision manufacturing, consistent quality, and building long-term client relationships.
            </p>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-navy-900 text-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Journey</h2>
            <p className="text-lg text-gray-300">
              Evolution through three decades of packaging excellence
            </p>
          </div>

          <div className="max-w-5xl mx-auto space-y-12">
            {timeline.map((item, index) => (
              <div 
                key={index}
                ref={el => timelineRef.current[index] = el}
                className={`flex flex-col md:flex-row items-center gap-8 ${
                  index % 2 === 0 ? '' : 'md:flex-row-reverse'
                }`}
              >
                <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                  <p className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    {item.era}
                  </p>
                  <h3 className="text-3xl font-bold mb-3">{item.title}</h3>
                  <p className="text-gray-300 leading-relaxed">{item.description}</p>
                </div>
                
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-white/10 border-4 border-white flex items-center justify-center">
                    <div className="w-4 h-4 rounded-full bg-white"></div>
                  </div>
                </div>
                
                <div className="flex-1"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Strengths */}
      <section className="py-24 bg-gray-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">
              Core Strengths
            </h2>
            <p className="text-lg text-gray-600">
              Capabilities that define our approach to packaging solutions
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {strengths.map((strength, index) => {
              const IconComponent = strength.icon;
              return (
                <Card 
                  key={index}
                  ref={el => strengthsRef.current[index] = el}
                  className="border-2 hover:shadow-xl transition-all duration-300"
                >
                  <CardContent className="p-6 space-y-4">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-navy-900 rounded-lg">
                      <IconComponent className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-navy-900">{strength.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{strength.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl font-bold text-navy-900">
              Our Commitment
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              We are committed to delivering packaging solutions that meet the highest standards of quality, 
              functionality, and reliability. Every project is an opportunity to demonstrate our expertise and 
              build lasting partnerships with our clients.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
